import { NavLink } from "react-router-dom";
import {
  LayoutDashboard,
  FileText,
  FolderTree,
  Search,
  Users,
  Shield,
  Database,
  Tag,
  ChevronRight,
  Grid3x3,
  Settings,
  Scale,
  LayoutTemplate,
  MessageSquare,
  Copy,
} from "lucide-react";
import { useRBAC } from "@/hooks/useRBAC";
import { useUIPrefsStore } from "@/store/uiPrefsSlice";
import { clsx } from "clsx";

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  badge?: string;
}

interface NavSection {
  label?: string;
  items: NavItem[];
}

export default function Sidebar() {
  const {
    canManageUsers,
    canViewAudit,
    canManageMaster,
    canManageTags,
    canViewDashboard,
    isGuest,
  } = useRBAC();
  const qnaMenuEnabled = useUIPrefsStore((s) => s.qnaMenuEnabled);

  const sections: NavSection[] = [
    {
      items: [
        ...(canViewDashboard
          ? [{ to: "/dashboard", label: "Dashboard", icon: LayoutDashboard }]
          : []),
        { to: "/documents", label: "Dokumen", icon: FileText },
        { to: "/documents/tree", label: "Struktur Dokumen", icon: FolderTree },
        { to: "/documents/matrix", label: "Pohon Pengetahuan", icon: Grid3x3 },
        // ...(!isGuest
        //   ? [{ to: "/pnbp-struktur", label: "Struktur PNBP", icon: Scale }]
        //   : []),
        ...(!isGuest
          ? [{ to: "/search", label: "Pencarian", icon: Search }]
          : []),
        ...(!isGuest && qnaMenuEnabled
          ? [{ to: "/knowledge", label: "Tanya AI (QNA)", icon: MessageSquare }]
          : []),
      ],
    },
    ...(canManageMaster
      ? [
          {
            label: "MASTER DATA",
            items: [
              { to: "/master", label: "Master Data", icon: Database },
              {
                to: "/master/taxonomy",
                label: "Master Taxonomy",
                icon: FolderTree,
              },
              { to: "/admin/duplikat", label: "Deteksi Duplikat", icon: Copy },
              ...(canManageTags
                ? [{ to: "/tags", label: "Tag", icon: Tag }]
                : []),
              ...(canManageUsers
                ? [{ to: "/users", label: "Pengguna", icon: Users }]
                : []),
              ...(canViewAudit
                ? [{ to: "/audit", label: "Audit Log", icon: Shield }]
                : []),
              {
                to: "/settings/connections",
                label: "Pengaturan",
                icon: Settings,
              },
            ],
          },
          {
            label: "CONTENT MANAGEMENT",
            items: [
              // { to: "/content/struktur-pnbp", label: "Struktur PNBP", icon: LayoutTemplate },
              {
                to: "/taxonomy-content",
                label: "Konten Aspek Taxonomy",
                icon: FolderTree,
              },
            ],
          },
        ]
      : [
          ...(!canManageTags
            ? []
            : [
                {
                  label: "PENGELOLAAN",
                  items: [{ to: "/tags", label: "Tag", icon: Tag }],
                },
              ]),
          ...(canManageUsers
            ? [
                {
                  label: "PENGELOLAAN",
                  items: [{ to: "/users", label: "Pengguna", icon: Users }],
                },
              ]
            : []),
          ...(canViewAudit
            ? [
                {
                  label: "ADMINISTRASI",
                  items: [{ to: "/audit", label: "Audit Log", icon: Shield }],
                },
              ]
            : []),
        ]),
  ];

  // Flatten all items for hasNestedItem check
  const allItems = sections.flatMap((s) => s.items);

  return (
    <aside className="w-60 shrink-0 border-r border-gray-200 bg-white flex flex-col">
      {/* Logo */}
      <div className="flex items-center gap-2.5 px-5 py-4 border-b border-gray-100">
        <div className="h-8 w-8 rounded-lg bg-blue-600 flex items-center justify-center flex-shrink-0">
          <FileText className="h-4 w-4 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-gray-900 leading-tight truncate">
            AI-PNBP
          </p>
          <p className="text-xs text-gray-400 leading-tight">
            Arsitektur Informasi PNBP
          </p>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-4">
        {sections.map((section, si) => (
          <div key={si}>
            {section.label && (
              <p className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-widest text-gray-400">
                {section.label}
              </p>
            )}
            <div className="space-y-0.5">
              {section.items.map((item) => {
                const hasNestedItem = allItems.some(
                  (other) =>
                    other.to !== item.to && other.to.startsWith(`${item.to}/`),
                );
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={hasNestedItem}
                    className={({ isActive }) =>
                      clsx(
                        "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors group",
                        isActive
                          ? "bg-blue-50 text-blue-700"
                          : "text-gray-600 hover:bg-gray-100 hover:text-gray-900",
                      )
                    }
                  >
                    {({ isActive }) => (
                      <>
                        <item.icon
                          className={clsx(
                            "h-4 w-4 flex-shrink-0",
                            isActive
                              ? "text-blue-600"
                              : "text-gray-400 group-hover:text-gray-600",
                          )}
                        />
                        <span className="flex-1">{item.label}</span>
                        {isActive && (
                          <ChevronRight className="h-3 w-3 text-blue-400" />
                        )}
                        {item.badge && (
                          <span className="rounded-full bg-red-100 px-1.5 py-0.5 text-xs text-red-600 font-semibold">
                            {item.badge}
                          </span>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer */}
      <div className="border-t border-gray-100 px-4 py-3">
        <p className="text-xs text-gray-400 text-center">v1.0.0 — PNBP KMS</p>
      </div>
    </aside>
  );
}
